import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, Search, Eye, Download, Loader2,
  MapPin, Globe, Cpu, Award, CheckCircle, XCircle,
  GraduationCap, User, Briefcase, Star, ChevronDown
} from 'lucide-react';

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]); 
  const [jobs, setJobs] = useState([]); 
  const [selectedJobId, setSelectedJobId] = useState(""); 
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showShortlistedOnly, setShowShortlistedOnly] = useState(false); 

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) { window.location.href = '/portal-gate-001'; return; }

      const headers = { 
        'Authorization': `Token ${token}`,
        'X-SECUWALL-NODE-ID': 'Alpha-9-Shield',
        'Content-Type': 'application/json',
        'Accept': 'application/json' 
      };
      
      try {
        // Fetching both Applications and Job Posts in parallel
        const [appRes, jobsRes] = await Promise.all([
          fetch('/api/job-applications/', { method: 'GET', headers }),
          fetch('/api/job-posts/', { method: 'GET', headers })
        ]);

        if (!appRes.ok || !jobsRes.ok) {
          throw new Error("One or more security nodes failed to respond.");
        }
      
        const appData = await appRes.json();
        const jobsData = await jobsRes.json();
        
        // Handle Applications data structure
        if (Array.isArray(appData)) {
          setApplications(appData);
        } else if (appData.results && Array.isArray(appData.results)) {
          setApplications(appData.results);
        }

        // Handle Job Posts data structure
        if (Array.isArray(jobsData)) {
          setJobs(jobsData); 
        } else if (jobsData.results && Array.isArray(jobsData.results)) {
          setJobs(jobsData.results);
        }

      } catch (err) {
        console.error("DATA_SYNC_FAILURE:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // --- HANDLERS ---
  const handleShortlist = async (e, id) => {
    e.stopPropagation();
    const token = localStorage.getItem('adminToken');

    setApplications(prev => 
      prev.map(app => app.id === id ? { ...app, is_shortlisted: !app.is_shortlisted } : app)
    );

    try {
      const res = await fetch(`/api/job-applications/${id}/toggle-shortlist/`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error("Backend synchronization failed");
    } catch (err) {
      console.error("SHORTLIST_SYNC_ERROR:", err);
      alert("Failed to sync shortlist status with node.");
    }
  };

  const handleSelect = (app) => {
    setSelectedApp(selectedApp?.id === app.id ? null : app);
  };

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return alert("No attachment detected for this node.");
    window.open(fileUrl, '_blank');
  };

  // --- FILTER LOGIC ---
  const filteredApps = applications.filter(app => {
    // 1. Search Logic (with safety fallbacks for null values)
    const name = (app.full_name || "").toLowerCase();
    const uni = (app.undergraduate_university_name || "").toLowerCase();
    const idNo = (app.id_no || "");
    const search = searchTerm.toLowerCase();
    const matchesSearch = name.includes(search) || uni.includes(search) || idNo.includes(search);
    
    // 2. Job Filter Logic (Robust check for object or ID)
    const appJobId = app.job?.id || app.job; 
    const matchesJob = selectedJobId === "" || String(appJobId) === String(selectedJobId);
    
    // 3. Shortlist Logic
    const matchesShortlist = showShortlistedOnly ? app.is_shortlisted : true;
  
    return matchesSearch && matchesJob && matchesShortlist;
  });

  return (
    <div className="min-h-screen bg-[#010805] text-green-500 font-mono">
      {/* NAVBAR */}
      <nav className="w-full border-b border-green-500/20 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 py-4 flex flex-col lg:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3 shrink-0">
            <ShieldCheck className="text-green-500 animate-pulse" size={28} />
            <h1 className="text-2xl font-black tracking-tighter text-white uppercase">
              SECUWALL_HR <span className="text-green-500/30 text-xs ml-2">V2.0.4</span>
            </h1>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-4 w-full justify-end">
            {/* SEARCH INPUT */}
            <div className="relative w-full md:w-[400px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500/30" size={16} />
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="SEARCH_BY_CANDIDATE_VARSITY_OR_ID..." 
                className="w-full bg-green-500/5 border border-green-500/20 rounded-full py-2 pl-10 pr-4 text-sm focus:border-green-500 focus:bg-green-500/10 outline-none transition-all" 
              />
            </div>

            {/* JOB FILTER SELECT */}
            <div className="relative w-full md:w-64">
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="w-full bg-green-500/5 border border-green-500/20 rounded-full py-2 px-4 pr-10 text-[10px] uppercase tracking-widest text-green-500 outline-none appearance-none focus:border-green-500 transition-all cursor-pointer"
              >
                <option value="" className="bg-[#010805]">ALL_JOB_POSTS</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id} className="bg-[#010805]">
                    {job.title}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-green-500/40" size={14} />
            </div>

            {/* SHORTLIST TOGGLE */}
            <button
              onClick={() => setShowShortlistedOnly(!showShortlistedOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] uppercase tracking-wider transition-all border shrink-0 ${
                showShortlistedOnly
                  ? "bg-green-500 text-black font-bold border-green-500"
                  : "border-green-500/20 hover:bg-green-500/10 text-green-500"
              }`}
            >
              <Star size={12} fill={showShortlistedOnly ? "black" : "none"} />
              {showShortlistedOnly ? "Shortlisted_Only" : "All_Nodes"}
            </button>

            <button 
              onClick={() => { localStorage.removeItem('adminToken'); window.location.href='/portal-gate-001'; }}
              className="text-[10px] border border-red-500/30 text-red-500 px-4 py-2 hover:bg-red-500/10 uppercase tracking-widest shrink-0"
            >
              Terminate_Session
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1800px] mx-auto p-4 space-y-8">
        <section className="space-y-4">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] px-2">
            <span>Primary_Data_Grid</span>
            <div className="flex gap-4">
              <span className="text-white/40">Filtered: {filteredApps.length}</span>
              <span>Total_Nodes: {applications.length}</span>
            </div>
          </div>
          
          <div className="bg-black border border-green-500/10 rounded-lg overflow-hidden shadow-[0_0_50px_rgba(0,0,0,1)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[1200px]">
                <thead>
                  <tr className="bg-green-500/5 text-[10px] uppercase text-green-500 font-black border-b border-green-500/20">
                    <th className="p-4 w-16">S/N</th>
                    <th className="p-4">Candidate_Name</th>
                    <th className="p-4">Undergraduate_Varsity</th>
                    <th className="p-4">Subject</th>
                    <th className="p-4">GPA</th>
                    <th className="p-4">Postgraduate_Varsity</th>
                    <th className="p-4">Key_Certifications</th>
                    <th className="p-4 text-center">Exp_Status</th>
                    <th className="p-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-500/5">
                  {loading ? (
                    <tr><td colSpan="9" className="p-24 text-center"><Loader2 className="animate-spin mx-auto mb-4" size={48}/>SYCHRONIZING_NODES...</td></tr>
                  ) : filteredApps.map((app, index) => (
                    <React.Fragment key={app.id}>
                      <tr 
                        onClick={() => handleSelect(app)}
                        className={`group transition-all cursor-pointer ${selectedApp?.id === app.id ? 'bg-green-500/10' : 'hover:bg-green-500/[0.03]'}`}
                      >
                        <td className="p-4 text-[11px] font-bold group-hover:opacity-100 transition-opacity">
                          {String(index + 1).padStart(2, '0')}
                        </td>
                        <td className="p-4 border-l-2 border-transparent group-hover:border-green-500 transition-all">
                          <div className="text-white font-bold text-sm uppercase">{app.full_name}</div>
                          <div className="text-[9px]">{app.email}</div>
                        </td>
                        <td className="p-4 text-xs font-medium">{app.undergraduate_university_name}</td>
                        <td className="p-4 text-xs">{app.undergraduate_subject}</td>
                        <td className="p-4 text-xs font-bold text-green-400">{app.undergraduate_result}</td>
                        <td className="p-4 text-xs italic">{app.post_graduate_university || 'N/A'}</td>
                        <td className="p-4 text-[10px]">
                          <span className="bg-white/5 border border-white/10 px-2 py-1 rounded">
                            {(app.certifications?.length > 0
                              ? app.certifications.map(c => c.title).join(', ')
                              : app.relevant_certifications
                            )?.substring(0, 20) || 'None'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          {app.experience ? 
                            <CheckCircle size={16} className="mx-auto text-green-500" /> : 
                            <XCircle size={16} className="mx-auto text-white/10" />
                          }
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-4">
                            <button 
                              onClick={(e) => handleShortlist(e, app.id)} 
                              className="transition-transform hover:scale-125 focus:outline-none"
                              title="Toggle Shortlist"
                            >
                              <Star 
                                size={18} 
                                fill={app.is_shortlisted ? "#22c55e" : "none"} 
                                className={app.is_shortlisted ? "text-green-500 shadow-[0_0_10px_#22c55e]" : "text-green-500/20"}
                              />
                            </button>
                            <div className={`p-2 rounded-full transition-all duration-300 ${selectedApp?.id === app.id ? 'bg-green-500 text-black rotate-180' : 'bg-white/5'}`}>
                              <Eye size={14} />
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* EXPANDED DETAILS */}
                      {selectedApp?.id === app.id && (
                        <tr className="bg-green-500/[0.02] border-y border-green-500/20">
                          <td colSpan="9" className="p-0">
                            <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-12 animate-in fade-in slide-in-from-top-4 duration-300">
                              
                              <div className="space-y-6">
                                <div className="flex items-center gap-2 text-[10px] text-green-500 uppercase font-bold border-b border-green-500/10 pb-2">
                                  <MapPin size={14}/> Location_&_Comms
                                </div>
                                <div className="space-y-4">
                                  <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-[10px]">MOBILE</span>
                                    <span className="text-sm text-white">{app.phone}</span>
                                  </div>
                                  <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-[10px]">IDENTITY_NO</span>
                                    <span className="text-sm text-white">{app.id_no}</span>
                                  </div>
                                  <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-[10px]">REGION</span>
                                    <span className="text-sm text-white">{app.nationality} ({app.gender})</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px]">CURRENT_RESIDENCE</span>
                                    <p className="text-sm text-white/80 mt-1 leading-relaxed">{app.present_address}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-6">
                                <div className="flex items-center gap-2 text-[10px] text-green-500 uppercase font-bold border-b border-green-500/10 pb-2">
                                  <GraduationCap size={14}/> Node_Academics_&_Experience
                                </div>
                                <div className="space-y-4">
                                  {app.experiences && app.experiences.length > 0 ? (
                                    <div className="space-y-3">
                                      <p className="text-[9px] text-white/30 uppercase font-black tracking-tighter">Professional_History:</p>
                                      {app.experiences.map((exp, idx) => (
                                        <div key={idx} className="bg-green-500/5 p-3 border-l border-green-500 rounded-r-lg">
                                          <p className="text-xs text-white font-bold uppercase">{exp.designation}</p>
                                          <p className="text-[10px] italic">{exp.institute_name} — {exp.period}</p>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-[10px] italic">No previous professional nodes detected.</p>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 pt-2">
                                    <div className="bg-black/40 p-2 rounded border border-white/5">
                                      <span className="text-[9px] block uppercase">{app.hsc_or_a_level}</span>
                                      <span className="text-xs text-white font-bold">GPA {app.hsc_or_a_level_result}</span>
                                    </div>
                                    <div className="bg-black/40 p-2 rounded border border-white/5">
                                      <span className="text-[9px] block uppercase">{app.ssc_or_o_level}</span>
                                      <span className="text-xs text-white font-bold">GPA {app.ssc_or_o_level_result}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-6">
                                <div className="flex items-center gap-2 text-[10px] text-green-500 uppercase font-bold border-b border-green-500/10 pb-2">
                                  <Award size={14}/> Validation_Dossier
                                </div>
                                <div className="space-y-4">
                                  {app.certifications && app.certifications.length > 0 ? (
                                    <div className="space-y-3">
                                      {app.certifications.map((cert, idx) => (
                                        <div key={idx} className="bg-green-500/10 p-4 rounded text-xs leading-relaxed text-green-100/70 border-l-2 border-green-500">
                                          <p className="text-white font-bold uppercase">{cert.title}</p>
                                          {cert.details && <p className="italic mt-1">{cert.details}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="bg-green-500/10 p-4 rounded italic text-xs leading-relaxed text-green-100/70 border-l-2 border-green-500">
                                      "{app.certifications_details || "No supplementary certification data available."}"
                                    </div>
                                  )}
                                  <button 
                                    onClick={() => handleDownload(app.cv_attachment)}
                                    className="w-full bg-green-500 text-black font-black py-4 rounded-sm flex items-center justify-center gap-3 hover:bg-green-400 hover:scale-[1.01] transition-all uppercase text-xs"
                                  >
                                    <Download size={18} /> Fetch_Raw_Resume_File
                                  </button>
                                  <div className="flex items-center justify-between text-[9px] pt-4">
                                    <span>TIMESTAMP: {new Date(app.timestamp || Date.now()).toLocaleString()}</span>
                                    <span>IP: {app.ip_address || '0.0.0.0'}</span>
                                  </div>
                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {!loading && filteredApps.length === 0 && (
          <div className="text-center py-20 italic">
            <Cpu size={48} className="mx-auto mb-4" />
            <p className="uppercase tracking-[0.5em] text-xs">No_Records_Found_In_Active_Node</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;